/**
 * The estate by status — ARCHITECTURE §16.7.
 *
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ **Why bars with a label on every row, and not a donut**              │
 * │                                                                      │
 * │ The four stat tiles above already answer "how many". The question    │
 * │ they cannot answer is *proportion* — whether 40 suspended SIMs is a  │
 * │ rounding error or a third of the estate. That is a magnitude         │
 * │ comparison across categories, which is a bar chart's job.            │
 * │                                                                      │
 * │ It is deliberately NOT a donut: reading a share off an arc is        │
 * │ guesswork past two or three slices, and the statuses here regularly  │
 * │ run to six.                                                          │
 * │                                                                      │
 * │ **Colour here is redundant, not load-bearing.** Every row states its │
 * │ status in words and repeats it as a glyph. That is what makes it     │
 * │ safe to draw marks from the status palette even though that palette  │
 * │ fails the validator's categorical gates: `warning` (#b54708) and     │
 * │ `error` (#b42318) measure ΔE 6.1 apart for normal colour vision,     │
 * │ well under the floor of 15. Two bars in those hues, side by side and │
 * │ unlabelled, would be genuinely indistinguishable. Labelled, the hue  │
 * │ is a second channel rather than the only one.                        │
 * ├──────────────────────────────────────────────────────────────────────┤
 * │ **Amended: the strip above the list, and why it is not "a stacked    │
 * │ bar"**                                                               │
 * │                                                                      │
 * │ This header used to rule out a single stacked bar outright, on the   │
 * │ grounds that three of our statuses (safe custody, suspended,         │
 * │ temporarily disconnected) share one tone and adjacent same-coloured  │
 * │ segments read as one segment with a hairline through it. That        │
 * │ objection was correct about *the chart being a stacked bar* and it   │
 * │ still holds — the labelled bar list below is still the chart, and    │
 * │ every value is still read from it.                                   │
 * │                                                                      │
 * │ What sits above it now is a different object: a decorative summary   │
 * │ of the same slices, in the same order, that answers "what is this    │
 * │ estate made of" before a word is read. The original objection is     │
 * │ answered rather than ignored — a 2px surface-coloured gap at every   │
 * │ join, and a 45°/135° hatch on the second and third slice of any      │
 * │ repeated tone, so three ambers in a row are three marks. It is       │
 * │ `aria-hidden`, it carries no value that is not also text below, and  │
 * │ nothing on the screen is read off it.                                │
 * │                                                                      │
 * │ Same argument for the ring in the header: one proportion (active     │
 * │ against the estate) against its own remainder, which is the one case │
 * │ an arc is good at. It is not a multi-slice donut and must not grow   │
 * │ into one.                                                            │
 * └──────────────────────────────────────────────────────────────────────┘
 */

import type { AccountStats } from '@rd/shared';
import type { ReactElement } from 'react';

import { Bar, Ring, Strip } from './marks.js';
import { TONE_ICON, TONE_TEXT, type ChartTone } from './tones.js';

/**
 * Re-exported from its new home rather than redefined.
 *
 * The tones moved to `tones.js` when the stat band, the composition strip and
 * the activity feed started drawing with the same five; a second copy of the
 * union would be a second place for the chart and the pills to drift apart,
 * which is precisely what §16.7 ruling B exists to prevent. The name stays
 * importable from here because that is where every caller and the spec that
 * checks the chart↔pill agreement already looks for it.
 */
export type { ChartTone } from './tones.js';

/**
 * The rows, in a fixed order, each naming the `AccountStats` field it reads.
 *
 * The tone assignments mirror `statusTone.ts` exactly — `ACTIVE: 'success'`
 * there is `active` here, `SAFE_CUSTODY: 'warning'` is `attention`, and so on.
 * They are written out rather than derived so that changing one is a decision
 * with a reason, and so this file states the mapping a reader can check against
 * the pills without opening another module.
 */
export const ROWS: {
  key: keyof AccountStats;
  label: string;
  tone: ChartTone;
  /**
   * The vendor's own spelling of this status. Present so a test can put this
   * row's tone next to `simStatusTone()`'s and fail if the chart and the pill
   * ever disagree — the two are read side by side on the same screen, so
   * "Active" being green in the table and amber in the chart is a defect the
   * type system cannot catch.
   */
  status: string;
}[] = [
  { key: 'activeSims', label: 'Active', tone: 'active', status: 'ACTIVE' },
  { key: 'availableSims', label: 'Available', tone: 'idle', status: 'AVAILABLE' },
  { key: 'testModeSims', label: 'Test mode', tone: 'progress', status: 'TEST_MODE' },
  { key: 'inProgressSims', label: 'In progress', tone: 'progress', status: 'IN_PROGRESS' },
  { key: 'safeCustodySims', label: 'Safe custody', tone: 'attention', status: 'SAFE_CUSTODY' },
  { key: 'suspendedSims', label: 'Suspended', tone: 'attention', status: 'SUSPENDED' },
  {
    key: 'tempDisconnectedSims',
    label: 'Temporarily disconnected',
    tone: 'attention',
    status: 'TEMP_DISCONNECTED',
  },
  { key: 'inactiveSims', label: 'Inactive', tone: 'idle', status: 'INACTIVE' },
];

export interface StatusSlice {
  label: string;
  tone: ChartTone;
  count: number;
  /** Percentage of the total estate, 0–100. */
  share: number;
}

/**
 * Turns the stats into the rows actually drawn.
 *
 * Two behaviours worth stating, both about not lying:
 *
 *  * A status with **no SIMs is dropped**, not drawn as a zero-length bar. An
 *    invisible mark beside a label reads as a rendering fault.
 *  * When the named statuses do not add up to `totalSims`, the remainder
 *    becomes an explicit **"Other"** row rather than being silently absorbed or
 *    left as a gap. §13.8: the vendor does not document its status vocabulary,
 *    so a status we have never seen is expected, and is information rather than
 *    an error. Without this row the shares would not sum to the total the tile
 *    above reports, and the operator would have no way to see why.
 */
export function statusSlices(stats: AccountStats): StatusSlice[] {
  const total = stats.totalSims;
  if (total <= 0) return [];

  const named = ROWS.map((row) => ({
    label: row.label,
    tone: row.tone,
    count: Number(stats[row.key] ?? 0),
  })).filter((row) => row.count > 0);

  const accounted = named.reduce((sum, row) => sum + row.count, 0);
  const remainder = total - accounted;
  const all =
    remainder > 0 ? [...named, { label: 'Other', tone: 'idle' as ChartTone, count: remainder }] : named;

  return all
    .map((row) => ({ ...row, share: (row.count / total) * 100 }))
    .sort((a, b) => b.count - a.count);
}

export function SimStatusChart({ stats }: { stats: AccountStats }): ReactElement | null {
  const slices = statusSlices(stats);
  if (slices.length === 0) return null;

  /*
    The ring's proportion is read from the stats rather than from the slice
    list, because a slice is absent when its count is zero: an estate with no
    active SIMs must draw an empty ring, not no ring at all.
  */
  const activeShare = (Number(stats.activeSims ?? 0) / stats.totalSims) * 100;

  return (
    <div
      // The brightness rule: the container holding real information is the one
      // bright plane on the canvas, framed in the strong hairline. `border-line`
      // is demoted to the interior dividers between rows.
      //
      // `.card` states that plane once. This panel sits directly beside the
      // stat band and the activity feed on the same screen, and three
      // hand-written copies of the same four utilities is three chances for one
      // of them to be a radius out of step with the other two.
      className="card p-[var(--space-5)]"
      data-testid="status-chart"
    >
      <div className="flex items-start justify-between gap-[var(--space-5)]">
        <div className="min-w-0">
          <h3 className="text-[length:var(--text-lg)] font-semibold tracking-[var(--tracking-tight)] text-body">
            Estate by status
          </h3>
          <p className="mt-1 text-[length:var(--text-xs)] tabular-nums text-muted">
            {stats.totalSims.toLocaleString()} SIMs
          </p>
        </div>

        {/*
          One proportion, drawn once. Both of the numbers inside it — the share
          and the word it describes — are already in the list below as text, so
          the whole block is hidden from assistive technology rather than read
          out twice.

          The percentage is split across two elements on purpose: an element
          whose text is exactly the same share string as a row below would be a
          second, identical piece of text on the same screen, and "which 50% is
          this?" is a question a graphic should never make a reader ask.
        */}
        <div aria-hidden="true" className="shrink-0">
          <div className="relative size-[72px]">
            <Ring share={activeShare} tone="active" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="text-[length:var(--text-lg)] font-semibold leading-[var(--leading-display)] tabular-nums text-body">
                {Math.round(activeShare)}
              </span>
              <span className="text-[length:var(--text-2xs)] text-muted">%</span>
            </span>
          </div>
          <p className="mt-[var(--space-1)] text-center text-[length:var(--text-2xs)] text-muted">
            active
          </p>
        </div>
      </div>

      {/*
        The composition, above the list it summarises — see the amendment in the
        file header for why this is not the chart. Slice order is identical to
        the list, so the eye can walk from a segment down to its label.
      */}
      <div className="mt-[var(--space-5)]">
        <Strip slices={slices} />
        <p className="mt-[var(--space-2)] text-[length:var(--text-2xs)] tabular-nums text-muted">
          {slices.length} {slices.length === 1 ? 'status' : 'statuses'} in this estate
        </p>
      </div>

      {/*
        A description list, not a <table> or an SVG. The numbers ARE the
        content: every value the chart encodes is present as text in the DOM, so
        a screen reader reads "Active, 100, 50%" without needing a separate
        table view, and the bars are decoration over the top of real data.
      */}
      <dl className="mt-[var(--space-5)] flex flex-col gap-[var(--space-2)] border-t border-line pt-[var(--space-4)]">
        {slices.map((slice) => {
          const Icon = TONE_ICON[slice.tone];
          return (
            <div
              key={slice.label}
              // The bar column is a **fixed** 5.5rem rather than a fraction, and
              // that is what preserves the one-shared-scale property the old
              // full-width track had: bars of different lengths under labels of
              // different widths cannot be compared, which is the whole point.
              // Fixing the column halves the panel's height without giving that
              // up.
              className="grid grid-cols-[1fr_5.5rem_auto] items-center gap-x-[var(--space-3)]"
            >
              <dt className="flex min-w-0 items-center gap-1.5 text-[length:var(--text-xs)] text-body">
                <Icon
                  aria-hidden="true"
                  size={13}
                  strokeWidth={2.25}
                  className={`shrink-0 ${TONE_TEXT[slice.tone]}`}
                />
                <span className="truncate">{slice.label}</span>
              </dt>

              {/*
                `Math.max(share, 1.5)` is kept from the original: a status
                holding six SIMs out of forty thousand draws to less than a
                pixel, and an invisible mark beside a label reads as a rendering
                fault rather than as "almost none". At the 88px column that
                floor is a visible ~1.3px sliver. The count and the true share
                are text in the next cell, so the floor cannot mislead.
              */}
              <dd className="m-0">
                <Bar share={Math.max(slice.share, 1.5)} tone={slice.tone} />
              </dd>

              <dd className="m-0 text-right text-[length:var(--text-xs)] tabular-nums text-muted">
                <span className="font-medium text-body">{slice.count.toLocaleString()}</span>{' '}
                <span>{formatShare(slice.share)}</span>
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

/**
 * A share, rounded for reading but never rounded to a lie.
 *
 * A status holding a handful of SIMs out of tens of thousands rounds to `0%`,
 * which reads as "none" when the row exists precisely because it is not none.
 * Anything above zero but below half a percent renders as `<1%`.
 */
export function formatShare(share: number): string {
  if (share <= 0) return '0%';
  if (share < 0.5) return '<1%';
  return `${Math.round(share)}%`;
}
